import { useMemo } from "react";
import { useMyFollowUps, type FollowUpWithRelations } from "@/hooks/use-follow-ups";
import { useSignalQueue } from "@/hooks/use-signal-queue";
import { splitQueue } from "@/lib/follow-up-calc";
import type { DBSignalQueueItem } from "@/lib/db-types";

/**
 * Køen i Oppdag. Oppfølgingene kommer først og må gjøres før nye signalkort
 * vises (CRM-3.5): `current` er første oppfølging, eller første signal når
 * ingen oppfølginger er igjen.
 */
export type QueueCard =
  | { kind: "follow_up"; followUp: FollowUpWithRelations }
  | { kind: "signal"; signal: DBSignalQueueItem };

export interface OppdagQueue {
  current: QueueCard | null;
  /** Åpne oppfølginger i køens rekkefølge. */
  openFollowUps: FollowUpWithRelations[];
  /** Fullført i uka, nyeste først. */
  doneFollowUps: FollowUpWithRelations[];
  remainingFollowUps: number;
  /** Signalkort i køen, inkludert det som vises nå. */
  signalsAvailable: number;
  isLoading: boolean;
}

/** `focusId` lar brukeren hoppe til et bestemt kort fra listen ved siden av. */
export function useOppdagQueue({ focusId }: { focusId?: string | null } = {}): OppdagQueue {
  const { data, isLoading } = useMyFollowUps();
  const { data: signals, isLoading: signalsLoading } = useSignalQueue();
  return useMemo(() => {
    const { open, done, current } = splitQueue(data ?? [], focusId);
    const nextSignal = signals?.[0];
    const card: QueueCard | null = current
      ? { kind: "follow_up", followUp: current }
      : nextSignal
        ? { kind: "signal", signal: nextSignal }
        : null;
    return {
      current: card,
      openFollowUps: open,
      doneFollowUps: done,
      remainingFollowUps: open.length,
      signalsAvailable: signals?.length ?? 0,
      isLoading: isLoading || (open.length === 0 && signalsLoading),
    };
  }, [data, signals, focusId, isLoading, signalsLoading]);
}
